<template>
  <transition name="apple-dialog" appear @leave="onLeave">
    <div
      v-if="open"
      ref="overlayRef"
      class="absolute inset-0 z-50 overflow-hidden"
      @click.stop
    >
      <div
        class="apple-dialog-backdrop absolute inset-0"
        :class="bgShadow && 'bg-black-opaced backdrop-blur-sm'"
        aria-hidden="true"
        @click="emit('close')"
      />
      <div
        class="apple-dialog-panel pointer-events-none absolute inset-0 flex items-center justify-center overflow-y-auto overflow-x-hidden"
      >
        <div
          v-if="position"
          class="pointer-events-auto flex flex-col"
          :style="{
            position: 'absolute',
            left: `${position.x}px`,
            top: `${position.y}px`,
          }"
          @click.stop
        >
          <slot />
        </div>
        <slot v-else />
      </div>
    </div>
  </transition>
</template>
<script setup lang="ts">
import { onBeforeUnmount, onUnmounted, ref, watch } from "vue";
import { playAppleDialogLeave } from "@/utils/appleMotion";
import { trackFullscreenModal } from "./fullscreenModalStack";

const props = withDefaults(
  defineProps<{
    open?: boolean;
    position?: {
      x: number;
      y: number;
    };
    bgShadow?: boolean;
  }>(),
  { bgShadow: false, open: true },
);

const emit = defineEmits(["close"]);
const overlayRef = ref<HTMLElement | null>(null);

function onLeave(el: Element, done: () => void) {
  playAppleDialogLeave(el as HTMLElement);
  done();
}

onBeforeUnmount(() => {
  if (overlayRef.value) playAppleDialogLeave(overlayRef.value);
});

function handleKeyDown(event: KeyboardEvent) {
  if (event.code === "Escape") emit("close");
}

let listening = false;

function setListening(on: boolean) {
  if (on === listening) return;
  listening = on;
  trackFullscreenModal(on);
  if (on) window.addEventListener("keydown", handleKeyDown);
  else window.removeEventListener("keydown", handleKeyDown);
}

watch(() => props.open, setListening, { immediate: true });

onUnmounted(() => setListening(false));
</script>

<style>
.body-no-scroll {
  overflow: hidden;
}
</style>
